/**
 * Custom element that listens for the 'variantsHandleGraphLoaded' event
 * published by <po-variant-handles-products> and renders color variant cards.
 */
class PoVariantColorCards extends HTMLElement {

  connectedCallback() {
    this.template = this.querySelector('[data-card-template]');
    this.container = this.querySelector('[data-js-cards-container]');

    this.titleEl = this.closest('.tw-po-modal-content').querySelector('.tw-po-modal-title');
    this.title = this.titleEl.textContent;

    if (window.variantsHandleGraphData?.loaded) {
      this.render(window.variantsHandleGraphData.products || []);
      this.updateModalTitle((window.variantsHandleGraphData.products || []).length);
    }

    window.addEventListener('variantsHandleGraphLoaded', (event) => {
      this.render((event.detail && event.detail.products) || []);
      this.updateModalTitle((event.detail && event.detail.products || []).length);
    });
  }

  /**
   * Updates the modal title with the number of products.
   * @param {number} qty - Number of products.
   */
  updateModalTitle(qty) {
    if (!this.titleEl) return;
    
    this.titleEl.textContent = this.title.replace('#', qty + 1);
  }

  /**
   * Renders a card for each product by cloning the template.
   * @param {Object[]} products - Array of product objects from the Storefront API.
   */
  render(products) {
    if (!this.template || !this.container || !products.length) return;

    this.container.innerHTML = '';

    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      
      const clone = this.template.content.cloneNode(true);

      const link = clone.querySelector('[data-card-link]');
      link.href = product.url;
      link.title = product.title;

      const color = product.color ? product.color.value : '';
      const lens = product.lensColorDescription ? product.lensColorDescription.value : '';
      const mocoAlt = (link.dataset.mocoAltBase || '')
        .replace('{frame}', color)
        .replace('{lenses}', lens);

      if (product.featuredImage) {
        const baseUrl = product.featuredImage.url;
        const image = clone.querySelector('[data-card-image]');
        image.src = PoVariantColorCards.getSizedImageUrl(baseUrl, 400);
        image.alt = mocoAlt;

        clone.querySelector('[data-card-source-tb]').srcset =
          PoVariantColorCards.getSizedImageUrl(baseUrl, 768);
        clone.querySelector('[data-card-source-sd]').srcset =
          PoVariantColorCards.getSizedImageUrl(baseUrl, 2000);
        clone.querySelector('[data-card-source-xlg]').srcset =
          PoVariantColorCards.getSizedImageUrl(baseUrl, 2000);
      }

      clone.querySelector('[data-card-available]').setAttribute('data-card-available', product.availableForSale);

      if (color) {
        const frameBlock = clone.querySelector('[data-card-frame]');
        frameBlock.hidden = false;
        frameBlock.querySelector('[data-card-frame-value]').innerHTML =
          PoVariantColorCards.formatDescription(color);
      }

      if (lens) {
        const lensBlock = clone.querySelector('[data-card-lens]');
        lensBlock.hidden = false;
        lensBlock.querySelector('[data-card-lens-value]').innerHTML =
          PoVariantColorCards.formatDescription(lens);
      }

      if (!color && !lens) {
        const titleEl = clone.querySelector('[data-card-title]');
        titleEl.hidden = false;
        titleEl.textContent = product.title;
      }

      const priceEl = clone.querySelector('[data-card-price]');
      const price = PoVariantColorCards.getDisplayPrice(product);
      if (product.marketPrice?.priceText) {
        priceEl.textContent = product.marketPrice.priceText;

        if (product.marketPrice.compareAtPrice > product.marketPrice.price) {
          const compareAtPriceWrapper = clone.querySelector('[data-card-compare-at-price-wrapper]');
          const compareAtPriceEl = clone.querySelector('[data-card-compare-at-price]');
          const discountBadge = clone.querySelector('[data-card-discount-badge]');
          const discountBadgeValue = clone.querySelector('[data-card-discount-badge-value]');
          const percentageOff = Math.round(((product.marketPrice.compareAtPrice - product.marketPrice.price) * 100) / product.marketPrice.compareAtPrice);

          compareAtPriceWrapper.hidden = false;
          compareAtPriceEl.textContent = product.marketPrice.compareAtPriceText;
          discountBadge.classList.remove('tw-hidden');
          discountBadgeValue.textContent = percentageOff + '% OFF';
        }
      } else if (price) {
        priceEl.textContent = PoVariantColorCards.formatPrice(price.amount, price.currencyCode);
      }

      console.log('[PoVariantColorCards]', product.handle, product.marketPrice);

      const priceDiscountNinjaEl = clone.querySelector('[data-card-price-discount-ninja]');
      priceDiscountNinjaEl.setAttribute('data-la-dn-product-handle', product.handle);

      this.container.appendChild(clone);
    }
  }

  /**
   * Returns the price of the first variant.
   * @param {Object} product - Product object.
   * @returns {Object|null} Price object with amount and currencyCode.
   */
  static getDisplayPrice(product) {
    const variants = product.variants.nodes;
    if (!variants.length) return null;
    return variants[0].price;
  }

  /**
   * Formats a price amount with its currency using the browser's locale.
   * @param {string} amount - The price amount (e.g. "150.00").
   * @param {string} currencyCode - The currency code (e.g. "EUR").
   * @returns {string} Formatted price string.
   */
  static formatPrice(amount, currencyCode) {
    const value = parseFloat(amount);
    if (isNaN(value)) return '';

    try {
      return new Intl.NumberFormat(window.Shopify.locale || 'en', {
        style: 'currency',
        currency: currencyCode
      }).format(value);
    } catch (e) {
      return currencyCode + ' ' + value.toFixed(2);
    }
  }

  /**
   * Appends a width parameter to a Shopify CDN image URL.
   * @param {string} url - Original image URL.
   * @param {number} width - Desired image width in pixels.
   * @returns {string} URL with width parameter.
   */
  static getSizedImageUrl(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url, location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch {
      return url;
    }
  }

  /**
   * Replicates the Liquid word-break logic for long descriptions.
   * Inserts a 
 after the second word when conditions are met.
   * @param {string} text - The description text.
   * @returns {string} HTML string with optional 
.
   */
  static formatDescription(text) {
    const words = text.split(' ');
    const secondWord = words.length > 1 ? words[1].trim() : '';

    if (text.length > 12 && words.length >= 3 && secondWord.length >= 4) {
      return words[0] + ' ' + words[1] + '<br>' + words.slice(2).join(' ');
    }

    return text;
  }
}

customElements.define('po-variant-color-cards', PoVariantColorCards); 